/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { isFormulaEmpty } from '@gh/builder-shared-data';
import {
  conditionalCellRenderer,
  createDefaultGridModel,
  falsyCellRenderer,
  GREEN_MARKER_COLOR,
  notEmptyCellRenderer,
  rendererColorCircle,
  rendererIconByName,
  ROW_DELETED_CLASS,
  ROW_DIRTY_CLASS,
  ROW_DIVIDER_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { NumberTypes, State, whenDirty } from '@gh/common-shared';
import {
  buildEditableNumberColumn,
  buildNumberColumn,
  gridCollectionComparator,
  GridModel,
  GridOptionsExt,
  LayoutBasedGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { composeComparators } from '@gh/core-util';
import { ColDef } from 'ag-grid-community';
import { get, negate } from 'lodash';
import {
  getHpeItemId,
  HpeItem,
  hpeItemFieldState,
  HpeItemType,
  isHpeItemFromComponent,
  validateHpeItemField,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import {
  HpeDetailsState,
  hpeItemComparator,
  queryHpeComponent,
  queryHpeEditable,
  queryHpeUomRefItem,
} from '../hpe-details.state';

const ROW_SALES_TAX_CLASS = 'row--sales-tax';

const ITEM_TYPE_ORDER = {
  [HpeItemType.Item]: 0,
  [HpeItemType.Component]: 0,
  [HpeItemType.Divider]: 1,
  [HpeItemType.SalesTax]: 2,
};

const itemTypeComparator = (left: HpeItem, right: HpeItem) => ITEM_TYPE_ORDER[left.type] - ITEM_TYPE_ORDER[right.type];

@Injectable()
export class HpeDetailsCcItemGridDefinition extends LayoutBasedGridDefinitionWithModel {
  constructor(private state: State<HpeDetailsState>,
              private commands: HpeCommandService) {
    super();
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel<HpeItem>({
      editable: this.state.project(queryHpeEditable),
      getId: getHpeItemId,
      validate: validateHpeItemField,
      getCellDef: hpeItemFieldState,
      command: (entity, name, value) => this.commands.costCenterItemSetField(getHpeItemId(entity), name, value),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
      defaultColDef: {
        sortable: false,
      },
      rowClassRules: {
        [ROW_DELETED_CLASS]: ({ data }: { data: HpeItem }) => data.isActive !== true,
        [ROW_DIRTY_CLASS]: ({ data }: { data: HpeItem }) => whenDirty(data)
          || isHpeItemFromComponent(data) && whenDirty(this.state.project(queryHpeComponent, data.hpComponentId!)!),
        [ROW_SALES_TAX_CLASS]: ({ data }: { data: HpeItem }) => data.type === HpeItemType.SalesTax,
        [ROW_DIVIDER_CLASS]: ({ data }: { data: HpeItem }) => data.type === HpeItemType.Divider,
      },
    };
  }

  buildColumnDefs(): Hash<ColDef> {
    return {
      cost: buildNumberColumn(NumberTypes.Price),
      extension: buildNumberColumn(NumberTypes.Price),
      quantity: buildEditableNumberColumn(NumberTypes.SignedQuantity, this.model.getCellAccessor('quantity')),
      code: {
        cellClassRules: {
          'warn-color': ({ data }: { data: HpeItem }) => data.type === HpeItemType.SalesTax,
        },
        sort: 'asc',
        comparator: gridCollectionComparator(composeComparators(itemTypeComparator, hpeItemComparator)),
      },
      uomCode: {
        valueFormatter: ({ data }: { data: any }) =>
          <string>get(this.state.project(queryHpeUomRefItem, data.uomId), 'code', ''),
      },
      isSalesTax: {
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
      },
      isComponent: {
        field: 'hpComponentId',
        cellRenderer: truthyCellRenderer(rendererColorCircle(GREEN_MARKER_COLOR)),
      },
      isCut: {
        field: 'cuttingList',
        cellRenderer: notEmptyCellRenderer(rendererColorCircle(GREEN_MARKER_COLOR)),
        valueGetter: this.itemInheritedValueGetter('cuttingList'),
      },
      isFormula: {
        field: 'formula',
        cellRenderer: conditionalCellRenderer(negate(isFormulaEmpty), rendererColorCircle(GREEN_MARKER_COLOR)),
        valueGetter: this.itemInheritedValueGetter('formula'),
      },
      isActive: {
        cellRenderer: falsyCellRenderer(rendererIconByName('done')),
      },
    };
  }

  /**
   * Value of some cells of component-based item can be inherited from the corresponding component
   */
  private itemInheritedValueGetter(field: string): any {
    return ({ data }: { data: HpeItem }) => (data.type === HpeItemType.Component
      ? this.state.project(queryHpeComponent, data.hpComponentId)!
      : data)[field];
  }
}
